<template>
  <v-layout column>
    <div class="headline mb-3">{{ $t('title') }}</div>
    <p>{{ $t('intro') }}</p>
    <v-layout row>
      <v-container grid-list-lg fluid align-start>
        <v-layout row wrap>
          <app-start-card :title="$t('sections.deploy.title')" img="deploy">
            <p>
              <i18n path="sections.deploy.content[0]">
                <a place="wizard" @click="openWizard()">{{ $t('sections.deploy.wizard') }}</a>
              </i18n>
            </p>
            <p>
              <i18n path="sections.deploy.content[1]">
                <a place="wizard" @click="openWizard()">{{ $t('sections.deploy.wizard') }}</a>
              </i18n>
            </p>
            <p>
              <i18n path="sections.deploy.content[2]">
                <router-link place="topology" to="/topology">{{ $t('sections.deploy.topology') }}</router-link>
              </i18n>
            </p>
          </app-start-card>
          <app-start-card :title="$t('sections.inject.title')" img="inject">
            <p>
              <i18n path="sections.inject.content[0]">
                <a place="ui" :href="gatherUrl" target="_blank">{{ $t('sections.inject.ui') }}</a>
              </i18n>
            </p>
            <p>{{ $t('sections.inject.content[1]') }}</p>
          </app-start-card>
          <app-start-card :title="$t('sections.visualize.title')" img="visualize">
            <p>
              <i18n path="sections.visualize.content[0]">
                <a place="portal" :href="reportsUrl" target="_blank">{{ $t('sections.visualize.portal') }}</a>
              </i18n>
            </p>
            <p>
              <i18n path="sections.visualize.content[1]">
                <router-link place="dashboards" to="/integrations">{{ $t('sections.visualize.dashboards') }}</router-link>
              </i18n>
            </p>
          </app-start-card>
          <app-start-card :title="$t('sections.analytics.title')" img="analytics">
            <p>
              <i18n path="sections.analytics.content[0]">
                <a place="ui" :href="alertUrl" target="_blank">{{ $t('sections.analytics.ui') }}</a>
                <a place="alarms" :href="alertsConsoleUrl" target="_blank">{{ $t('sections.analytics.alarms') }}</a>
              </i18n>
            </p>
          </app-start-card>
          <app-start-card :title="$t('sections.customize.title')" img="customize">
            <p>{{ $t('sections.customize.content[0]') }}</p>
            <p>
              <i18n path="sections.customize.content[1]">
                <a place="tag" :href="docsUrl.tag" target="_blank">{{ $t('sections.customize.tag') }}</a>
                <a place="tenants" :href="docsUrl.tokens" target="_blank">{{ $t('sections.customize.tenants') }}</a>
                <a place="Kubernetes" :href="docsUrl.kubernetes" target="_blank">Kubernetes</a>
                <a place="dashboards" :href="docsUrl.dashboards" target="_blank">{{ $t('sections.customize.dashboards') }}</a>
                <a place="api" :href="docsUrl.api" target="_blank">{{ $t('sections.customize.api') }}</a>
              </i18n>
            </p>
          </app-start-card>
          <app-start-card :title="$t('sections.manage.title')" img="manage">
            <p>
              <i18n path="sections.manage.content[0]">
                <router-link place="console" to="/overview">{{ $t('sections.manage.console') }}</router-link>
              </i18n>
            </p>
            <p>
              <i18n path="sections.manage.content[1]">
                <a place="learn" :href="introductionUrl" target="_blank">{{ $t('sections.manage.learn') }}</a>
              </i18n>
            </p>
          </app-start-card>
        </v-layout>
        <app-deploy :open="deploy" @close="closeWizard"></app-deploy>
      </v-container>
    </v-layout>
  </v-layout>
</template>

<script>
import CardComponent from '@/components/start/Card.vue'
import DeployComponent from '@/components/Deploy.vue'
import { docsUrl } from '@/services/docs'
import NetworkService from '@/services/network'

export default {
  data () {
    return {
      selected: null,
      docsUrl: {
        tag: docsUrl('model', 'use-the-unryo-tagger'),
        tokens: docsUrl('manage', 'manage-tokens'),
        kubernetes: docsUrl('quickstart', 'kubernetes-helm'),
        dashboards: docsUrl('portal'),
        api: docsUrl('api')
      },
      gatherUrl:  docsUrl('quickstart', 'enable-collect'),
      reportsUrl: docsUrl('quickstart', 'start-visualizing-your-data'),
      alertUrl: docsUrl('analytics', 'setting-up-alerts'),
      alertsConsoleUrl: docsUrl('alerts'),
      introductionUrl: docsUrl('introduction'),
      deploy: false
    }
  },
  mounted () {
    NetworkService.nodes().then((nodes) => {
      // grab the first node providing reports and use its url
      const reports = nodes.find((node) => node.group === 'report' && node.url)
      if (reports) {
        this.reportsUrl = reports.url
      }
    })
  },
  methods: {
    openWizard () {
      this.deploy = true
      this.$root.$emit('wizardOpen', 'The wizard is now active.')
    },
    closeWizard () {
      this.deploy = false
      this.$root.$emit('wizardClose', 'The wizard is now closed.')
    }
  },
  components: {
    'app-deploy': DeployComponent,
    'app-start-card': CardComponent
  }
}
</script>

<style lang="scss" scoped>
.container {
  height: 100%;
}

a {
  color: var(--v-accent-base);
  text-decoration: underline;
}
</style>

<i18n>
{
  "en": {
    "title": "Get Started with Unryo",
    "intro": "To learn and get the most out of Unryo, we suggest you complete the following steps:",
    "sections": {
      "deploy": {
        "title": "Deploy Components",
        "content": [
          "If you're a SaaS customer, deploy your first collector in a few clicks using the {wizard}.",
          "If you prefer to host Unryo on prem, use that same {wizard} to deploy every platform component.",
          "Everything will auto connect instantly and securely. Go see your topology in the {topology}."
        ],
        "wizard": "wizard",
        "topology": "Topology Map"
      },
      "inject": {
        "title": "Inject Data in Unryo",
        "content": [
          "Let's inject data by collecting some metrics, logs or application traces. Get it done with a few clicks from the {ui}.",
          "Why is it so simple? Because your configurations will be encrypted in a secure vault and the Unryo connectivity layer will distribute them to collectors automatically."
        ],
        "ui": "UI"
      },
      "visualize": {
        "title": "Start Visualizing Your Data",
        "content": [
          "Connect to the {portal} and browse your dashboards, analyze your logs, troubleshoot your applications.",
          "We've built numerous ready-to-use {dashboards} that cover the major technologies for you."
        ],
        "dashboards": "dashboards",
        "portal": "Portal"
      },
      "analytics": {
        "title": "Activate Analytics & Alerting",
        "content": [
          "Go in the {ui} to pick and choose the analytics rules you want to enable, refine the alerting conditions, and configure alert actions (Slack, Emails, Teams, ...). See and manage your alerts with the {alarms}."
        ],
        "ui": "UI",
        "alarms": "Alarm Console"
      },
      "customize": {
        "title": "Customize Your Monitoring",
        "content": [
          "Unryo is a telco grade platform with a high degree of customizations and automation.",
          "{tag} your data, configure {tenants}, integrate with your {Kubernetes}, create your own {dashboards}. Or use our {api} to programmatically interact with the platform."
        ],
        "tag": "Tag",
        "tenants": "multi-tenancy",
        "dashboards": "dashboards",
        "api": "API"
      },
      "manage": {
        "title": "Manage Your Platform",
        "content": [
          "Unryo is a near zero maintenance platform. See how the {console} gives you real time visibility on the health and status of all components.",
          "{learn} how to fine tune the platform to your setup and requirements."
        ],
        "console": "Connect Console",
        "learn": "Learn"
      }
    }
  }
}
</i18n>
